import { Alert } from '@creditornot/cb-alert';
import { Button, IconButton } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { EyeOff, EyeFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, Elevation } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  createOrderApiApiKey,
  resetOrderApiApiKey,
} from 'apis/restaurant-api/merchant-admin/order-api-credentials/api';
import {
  OrderApiCredentials,
  OrderApiKeyResponse,
} from 'apis/restaurant-api/merchant-admin/order-api-credentials/types';
import { WoltModal } from 'common/components';

type ManageApiKeyModalProps = {
  orderApiMetadata: OrderApiCredentials;
  merchantId: string;
  isOpen: boolean;
  onClose: () => void;
};

const StyledModal = styled(WoltModal)`
  width: 100%;
  max-width: 640px;
`;

const Container = styled(Elevation).attrs({ level: 16 })`
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const ModalButton = styled(Button)`
  margin: 0 8px;
`;

const Eye = styled.span``;

const PasswordInputWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 8px;

  ${Eye} {
    margin-inline-start: 8px;
  }
`;

const StyledFormRow = styled(FormRow)`
  & > div:first-child {
    width: inherit;
  }
`;

const ErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  padding: 8px 0 0;
`;

const OrderApiManageApiKeyModal = ({ onClose, isOpen, merchantId, orderApiMetadata }: ManageApiKeyModalProps) => {
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState<OrderApiKeyResponse | null>(null);

  const mutation = useMutation(
    () => {
      if (orderApiMetadata?.api_key_id) {
        return resetOrderApiApiKey(merchantId, orderApiMetadata.id, orderApiMetadata.api_key_id);
      } else {
        return createOrderApiApiKey(merchantId, orderApiMetadata.id);
      }
    },
    {
      onSuccess: result => {
        setCurrentApiKey(result);
      },
    },
  );

  return (
    <StyledModal isOpen={isOpen}>
      <Container>
        <ModalHeader onCloseClick={onClose}>
          {orderApiMetadata?.api_key_id ? (
            <FormattedMessage id="merchant-integrations.order-api.api-key.reset-title" />
          ) : (
            <FormattedMessage id="merchant-integrations.order-api.api-key.setup-title" />
          )}
        </ModalHeader>
        <ModalContent>
          {currentApiKey && (
            <>
              <FormattedMessage id="merchant-integrations.order-api.api-key.generation-notice" />

              <StyledFormRow
                horizontalPadding
                label={(<FormattedMessage id="merchant-integrations.order-api.api-key.title" />) as any}
                labelAlign="center"
              >
                <PasswordInputWrapper>
                  <Input value={currentApiKey.api_key} type={apiKeyVisible ? 'text' : 'password'} readOnly />
                  <Eye>
                    {apiKeyVisible ? (
                      <IconButton
                        variant="black"
                        size="small"
                        background="staticWhite"
                        onClick={() => setApiKeyVisible(false)}
                      >
                        <EyeOff />
                      </IconButton>
                    ) : (
                      <IconButton
                        variant="black"
                        size="small"
                        background="staticWhite"
                        onClick={() => setApiKeyVisible(true)}
                      >
                        <EyeFilled />
                      </IconButton>
                    )}
                  </Eye>
                </PasswordInputWrapper>
              </StyledFormRow>
            </>
          )}
          {!currentApiKey && (
            <>
              {orderApiMetadata?.api_key_id ? (
                <FormattedMessage id="merchant-integrations.order-api.api-key.reset-notice" />
              ) : (
                <FormattedMessage id="merchant-integrations.order-api.api-key.setup-notice" />
              )}
            </>
          )}
          {mutation.isError && (
            <ErrorMessage>
              <FormattedMessage id="errors.unknown-error-retry-contact"></FormattedMessage>
            </ErrorMessage>
          )}
        </ModalContent>
        <StyledModalFooter>
          <ModalButton
            variant={currentApiKey ? 'blue' : 'lightBlue'}
            size="small"
            onClick={() => {
              if (currentApiKey) {
                setCurrentApiKey(null);
              }
              onClose();
            }}
            disabled={mutation.isLoading}
            loading={mutation.isLoading}
          >
            {currentApiKey ? <FormattedMessage id="state.done" /> : <FormattedMessage id="state.cancel" />}
          </ModalButton>

          {!currentApiKey && (
            <ModalButton
              variant={orderApiMetadata?.api_key_id ? 'lightRed' : 'blue'}
              size="small"
              disabled={mutation.isLoading}
              loading={mutation.isLoading}
              onClick={() => {
                if (currentApiKey) {
                  setCurrentApiKey(null);
                  onClose();
                } else {
                  mutation.mutate();
                }
              }}
            >
              {orderApiMetadata?.api_key_id ? (
                <FormattedMessage id="merchant-integrations.order-api.api-key.reset-action" />
              ) : (
                <FormattedMessage id="merchant-integrations.order-api.generate-action" />
              )}
            </ModalButton>
          )}
        </StyledModalFooter>
      </Container>
    </StyledModal>
  );
};

export default OrderApiManageApiKeyModal;
